// InviteModal.tsx

import { useAsset } from "@/hooks/useAsset";
import { useGetGuild } from "@/hooks/useGetGuild";
import { FC, useState } from "react";
import { IoMdClose } from "react-icons/io";
import FrameModalBody from "./modals/modalComponents/FrameModalBody";
import FrameOneModal from "./modals/modalComponents/FrameOneModal";
import FrameButton from "./buttons/FrameButton";
import { useGetOwnReferralCode } from "@/hooks/useGetOwnReferralCode";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { FaCheck, FaFacebook, FaTwitter } from "react-icons/fa";
import { MdContentCopy, MdEmail } from "react-icons/md";
import { isGoNative } from "@/utils/isGoNative";
import { TbMessageCircleFilled } from "react-icons/tb";
import useThemePreference from "@/hooks/useThemePreference";
import { FiShare } from "react-icons/fi";
import { isMobile } from "react-device-detect";
import { EmailShareButton, TwitterShareButton } from "@ciaran0/react-share";
import { FacebookShareModal } from "./FacebookShareModal";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";
import { isColorDarkOrLight } from "@/utils/isColorDarkOrLight";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

export enum InviteModalType {
    GUILD = "GUILD",
    CHALLENGE = "CHALLENGE",
    STANDARD = "STANDARD",
}

interface props {
    type?: InviteModalType;
    guildId?: string;
    challengeId?: string;
    alwaysUseDarkBackground?: boolean;
    invitePageUrl?: string;
    isVisible: boolean;
    onClose: () => void;
}

export const InviteModal: FC<props> = ({ isVisible, onClose, guildId, type = InviteModalType.STANDARD, invitePageUrl = "/", alwaysUseDarkBackground = false, challengeId }) => {
    const guild = useGetGuild(guildId);
    const challenge = useGetChallenge(challengeId);
    const guildAssetImage = useAsset(guild?.data?.hero);
    const challengeAssetImage = useAsset(challenge?.data?.backgroundImageSmall);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { copyToClipboard } = useCopyToClipboard();
    const [isTextCopied, setIsTextCopied] = useState(false);
    const [isFacebookShareModalVisible, setIsFacebookShareModalVisible] = useState(false);
    const { isOnDarkMode } = useThemePreference();
    const { data: ownReferralCodeData } = useGetOwnReferralCode(fullToken);
    const referralCode = ownReferralCodeData?.referralCodes?.[0]?.code;

    const getInviteLink = () => {
        switch (type) {
            case InviteModalType.GUILD:
                return `${import.meta.env.VITE_WEB_URL}/guilds?ref=${encodeURI(referralCode)}&guildId=${guild?.data?.id}`;
            case InviteModalType.CHALLENGE: {
                const baseUrl = challenge?.data?.brandMapSlug ? `/brands/${challenge?.data?.brandMapSlug}` : `/challenges/${challengeId}`;
                return `${import.meta.env.VITE_WEB_URL}${baseUrl}?ref=${encodeURI(referralCode)}&challengeId=${challenge?.data?.id}`;
            }
            case InviteModalType.STANDARD:
                return `${import.meta.env.VITE_WEB_URL}${invitePageUrl}?ref=${encodeURI(referralCode)}`;
            default:
                return "";
        }
    };

    const getDescription = () => {
        switch (type) {
            case InviteModalType.GUILD:
                return `Invite your friends to join your DevourPlay guild ${guild?.data?.name}) and earn referral rewards!`;
            case InviteModalType.CHALLENGE:
                return `Invite your friends to join the ${challenge?.data?.name}`;
            case InviteModalType.STANDARD:
                return "Invite your friends to join Devour and earn referral rewards for both you and your friend!";
            default:
                return "";
        }
    };

    const getHeroUrl = () => {
        switch (type) {
            case InviteModalType.GUILD:
                return guildAssetImage?.data?.cdnUrl;
            case InviteModalType.CHALLENGE:
                return challengeAssetImage?.data?.cdnUrl;
            case InviteModalType.STANDARD:
                return ""; // Clean background for standard invites
            default:
                return "";
        }
    };

    const getShareMessage = (includeLink = true) => {
        const link = includeLink ? `: ${getInviteLink()}` : "";
        switch (type) {
            case InviteModalType.STANDARD:
                return "👋 Join DevourPlay and Start Earning Rewards";
            case InviteModalType.CHALLENGE:
                return `Join me in the ${challenge?.data?.name} on Devour`;
            case InviteModalType.GUILD:
                return `Join me as I am levelling up my ${guild?.data?.name} guild on DevourPlay! Use my link to sign up now ${link} 🚀`;
            default:
                return "";
        }
    };

    const getSocialQuote = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return "I’m using Devour to earn rewards just for gaming — XP, Fuel, lootboxes, even gear. If you’re gaming anyway, might as well earn while you do!\n\nCheck it out! 👉";
            case InviteModalType.CHALLENGE:
                return `🎮 Just jumped into the ${challenge?.data?.name} on Devour.\nQuests, instant rewards, & solid prize pool. 🏆\n\nCome run it with me! 😎 👉`;
            case InviteModalType.GUILD:
                return `🎮 I’m repping the ${guild?.data?.name} Guild on Devour!\n\nMonthly prizes for: \n\n🏆 Winning Guild\n🎖️ Top Guild XP earner\n👑 Top XP overall\n\nLet’s run it! 💪🎯\n👉 [Join My Guild Here]`;
            default:
                return "";
        }
    };

    const getSocialHashtags = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return [];
            case InviteModalType.CHALLENGE:
                return ["#BrandChallenge", "#GamingRewards"];
            case InviteModalType.GUILD:
                return [];
            default:
                return [];
        }
    };


    const getEmailData = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return {
                    subject: "👋 Join DevourPlay and Start Earning Rewards",
                    body: `I’m using Devour to earn rewards just for gaming — XP, Fuel, lootboxes, even gear. If you’re gaming anyway, might as well earn while you do!\n\nCheck it out! 👉 ${getInviteLink()}`,
                };
            case InviteModalType.CHALLENGE:
                return {
                    subject: `Join me in the ${challenge?.data?.name} on Devour`,
                    body: `Hey!\nI just jumped into the ${challenge?.data?.name} on Devour — complete quests, grab instant rewards, and score a shot at some solid prizes 🏆\n\nThought you might want in too. Could be fun to see who finishes it first 😎\n\nHere’s the link to join: ${getInviteLink()}`,
                };
            case InviteModalType.GUILD:
                return {
                    subject: `🎮 Squad Up! Join My ${guild?.data?.name} Guild on Devour and Start Earning Rewards`,
                    body: `👋 Hey! Come join me in the ${guild?.data?.name} Guild on Devour — the platform that rewards us just for playing games.\n\nEvery month is a competition, with prizes for:\n🏆 All winning Guild members\n🎖️ The top XP earner in the winning Guild\n👑 The top overall XP earner across all players\n\nLet’s squad up and start stacking rewards together. 💪\n\n👉 [Join My Guild Here](${getInviteLink()})\n\nSee you in-game! 🎮`,
                };
            default:
                return "";
        }
    };

    const getSmsBody = () => {
        switch (type) {
            case InviteModalType.STANDARD:
                return `I’m using Devour to earn rewards just for gaming — XP, Fuel, lootboxes, even gear. If you’re gaming anyway, might as well earn while you do!\n\nCheck it out! 👉 ${getInviteLink()}`;
            case InviteModalType.CHALLENGE:
                return `I just joined the [NAME OF CHALLENGE] on Devour 🎮\nQuests, instant rewards, and a shot at the prize pool 🏆\nCome run it with me 😎 ${getInviteLink()}`;
            case InviteModalType.GUILD:
                return `👋 Hey! Join me in the ${guild?.data?.name} Guild on Devour.\nWe earn rewards just for gaming + monthly prizes too.\nHop in here:\n${getInviteLink()}`;
            default:
                return "";
        }
    };

    const getChallengeButtonStyles = (): any => {
        if (type === InviteModalType.CHALLENGE) {
            const textColor = isColorDarkOrLight(challenge?.data?.brandColor) === "dark" ? "#FFFFFF" : "#282438";
            return {
                backgroundColor: isTextCopied ? undefined : challenge.data?.brandColor,
                color: isTextCopied ? undefined : textColor,
            };
        }
        return undefined;
    };


    const shareOptions = [
        {
            icon: <TbMessageCircleFilled />,
            title: "Message",
            onClick: () => {
                window.open(`sms:?&body=${encodeURIComponent(getSmsBody())}`);
            },
        },
        {
            icon: <MdEmail />,
            title: "Email",
            render: () => {
                const emailData = getEmailData();
                return (
                    <EmailShareButton
                        url={" "}
                        subject={emailData.subject}
                        body={emailData.body}
                        className="invite-modal_body_content_share-options_option_content"
                        style={{ backgroundColor: "none" }}
                    >
                        <MdEmail />
                    </EmailShareButton>
                );
            },
        },
        {
            icon: <FaTwitter />,
            title: "Twitter",
            render: () =>
                <TwitterShareButton
                    title={getSocialQuote()}
                    url={getInviteLink()}
                    hashtags={getSocialHashtags()}
                    className="invite-modal_body_content_share-options_option_content"
                    style={{ backgroundColor: "none" }}
                >
                    <FaTwitter />
                </TwitterShareButton>,
        },
        {
            icon: <FaFacebook />,
            title: "Facebook",
            render: () =>
                <div onClick={() => setIsFacebookShareModalVisible(true)} className="invite-modal_body_content_share-options_option_content">
                    <FaFacebook />
                </div>,
        },
        {
            icon: <FiShare />,
            onlyOnMobile: true,
            title: "Share",
            onClick: () => {
                if (isGoNative()) {
                    // @ts-ignore
                    gonative.share.sharePage({ url: getInviteLink(), text: getShareMessage(false) });
                } else {
                    navigator.share({
                        title: "I am inviting you to DevourPlay!",
                        text: getShareMessage(false),
                        url: getInviteLink(),
                    });
                }
            },
        },
    ];

    const handleCopyLink = () => {
        if (isTextCopied) return;
        setIsTextCopied(true);
        copyToClipboard(getInviteLink());
        setTimeout(() => setIsTextCopied(false), 10000);
    };

    return (
        <>
            <FacebookShareModal
                hashtag={getSocialHashtags().join(" ")}
                onClose={() => setIsFacebookShareModalVisible(false)}
                isVisible={isFacebookShareModalVisible}
                link={getInviteLink()}
                quote={getSocialQuote()}
            />
            <FrameOneModal size="xs1" isOpen={isVisible} toggle={onClose} contentClassName="invite-modal">
                <div className="invite-modal_background" style={{
                    backgroundImage: getHeroUrl() ? `url(${getHeroUrl()})` : undefined,
                    background: !getHeroUrl() ? "linear-gradient(135deg, #2a2a3e, #1e1e2d)" : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    height: "8.73556rem",
                }} />
                <div className="invite-modal_shader" style={{
                    background: `linear-gradient(180deg, rgba(45, 45, 47, 0.05) 0%, ${
                        isOnDarkMode || alwaysUseDarkBackground ? "rgba(45, 45, 47, 0.95)" : "white"
                    } 6.5rem)`,
                }} />
                <IoMdClose className="invite-modal_close" onClick={onClose} />
                <FrameModalBody className="invite-modal_body">
                    <div className="invite-modal_body_heading">
                        <h5>Invite Your Friends</h5>
                        <span>{getDescription()}</span>
                    </div>
                    <div className="invite-modal_body_content">
                        <div className="invite-modal_body_content_field">
                            <input value={getInviteLink()} readOnly />
                            <FrameButton
                                color={isTextCopied ? "gray" : "purple"}
                                onClick={handleCopyLink}
                                rightIcon={isTextCopied ? FaCheck : MdContentCopy}
                                size="narrow"
                                forwardProps={{
                                    style: getChallengeButtonStyles(),
                                }}
                            >
                                {isTextCopied ? "Copied" : "Copy"}
                            </FrameButton>
                        </div>
                        <div className="invite-modal_body_content_share-options">
                            {shareOptions
                                .filter(option => !(option.onlyOnMobile && !isMobile))
                                .map((option, index) =>
                                    <div
                                        key={index + option.title}
                                        className="invite-modal_body_content_share-options_option"
                                        onClick={option.onClick}
                                    >
                                        {option.render ? option.render()
                                            : <div className="invite-modal_body_content_share-options_option_content">
                                                {option.icon}
                                            </div>
                                        }
                                        <span>{option.title}</span>
                                    </div>)}
                        </div>
                    </div>
                </FrameModalBody>
            </FrameOneModal>
        </>
    );
};
